import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import KanbanCard, { type KanbanTicket } from './KanbanCard';

interface Column {
  id: string;
  title: string;
  color: string;
}

interface KanbanColumnProps {
  column: Column;
  tickets: KanbanTicket[];
  onAddTicket: () => void;
}

export default function KanbanColumn({ column, tickets, onAddTicket }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div
      className={`flex w-[270px] min-w-[270px] flex-shrink-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm transition-all md:w-[280px] md:min-w-[280px] ${
        isOver ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-[#f5f5f7]' : ''
      }`}
    >
      {/* Colored top indicator */}
      <div className={`h-1 ${column.color}`} />

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-800">{column.title}</h3>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-500">
            {tickets.length}
          </span>
        </div>
      </div>

      {/* Cards container */}
      <div
        ref={setNodeRef}
        className="max-h-[calc(100vh-340px)] min-h-[80px] flex-1 space-y-2 overflow-y-auto px-2.5 pb-2.5"
      >
        <SortableContext items={tickets.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tickets.map(ticket => (
            <KanbanCard key={ticket.id} ticket={ticket} />
          ))}
        </SortableContext>
      </div>

      {/* Add button */}
      <button
        type="button"
        onClick={onAddTicket}
        className="mx-2.5 mb-2.5 flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-200 py-2 text-sm text-gray-400 transition-all hover:border-green-300 hover:bg-green-50 hover:text-green-600"
      >
        <Plus size={14} />
        Adicionar
      </button>
    </div>
  );
}
